import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle, ArrowRight, Crown, Target, Users, Zap, HelpCircle } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const authFetch = (url, options = {}) => {
  const sid = localStorage.getItem("session_id");
  const headers = {
    ...(options.headers || {}),
    ...(sid ? { "X-Session-ID": sid } : {}),
  };
  return fetch(url, { credentials: "include", ...options, headers });
};

const GOALS = [
  { id: "more_local", label: "Get more local customers finding me through AI", icon: "📍" },
  { id: "outrank",    label: "Outrank a specific competitor in AI results",    icon: "🏆" },
  { id: "more_ai",    label: "Appear in more AI search answers overall",       icon: "🤖" },
  { id: "new_area",   label: "Expand into a new area or service category",     icon: "🚀" },
];

const SOURCES = [
  { id: "google",    label: "Google Search" },
  { id: "ai_search", label: "AI Search (ChatGPT, Perplexity, etc.)" },
  { id: "referral",  label: "Referral from someone I know" },
  { id: "social",    label: "Social Media" },
  { id: "blog",      label: "Blog or Article" },
  { id: "affiliate", label: "Affiliate / Partner" },
  { id: "other",     label: "Other" },
];

const PaymentSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const checkoutSessionId = searchParams.get("session_id");

  const [verifying, setVerifying] = useState(true);
  const [plan, setPlan] = useState(null);
  const [overlayStep, setOverlayStep] = useState(1); // 1-4
  const [overlayDone, setOverlayDone] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [personalization, setPersonalization] = useState({
    competitors: ["", "", "", "", ""],
    primary_goal: "",
    acquisition_source: "",
    additional_context: "",
  });

  // Verify payment on mount
  useEffect(() => {
    if (!checkoutSessionId) { navigate("/pricing"); return; }
    verifyPayment();
  }, [checkoutSessionId]);

  const verifyPayment = async () => {
    try {
      const res = await authFetch(`${API}/payments/status/${checkoutSessionId}`);
      if (res.ok) {
        const data = await res.json();
        setPlan(data.tier);
      } else {
        toast.error("Could not verify payment. Please contact support.");
      }
    } catch {
      toast.error("Network error verifying payment.");
    } finally {
      setVerifying(false);
    }
  };

  const updateCompetitor = (i, val) => {
    setPersonalization((p) => {
      const competitors = [...p.competitors];
      competitors[i] = val;
      return { ...p, competitors };
    });
  };

  const canAdvance = () => {
    if (overlayStep === 1) return true; // competitors optional
    if (overlayStep === 2) return !!personalization.primary_goal;
    if (overlayStep === 3) return !!personalization.acquisition_source;
    return true;
  };

  const handleNext = () => {
    if (!canAdvance()) { toast.error("Please make a selection to continue"); return; }
    if (overlayStep < 4) setOverlayStep(overlayStep + 1);
  };

  const handleFinish = async () => {
    setSubmitting(true);
    try {
      const payload = {
        competitors: personalization.competitors.filter((c) => c.trim()),
        primary_goal: personalization.primary_goal,
        acquisition_source: personalization.acquisition_source,
        additional_context: personalization.additional_context,
      };
      await authFetch(`${API}/onboarding/personalization`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      setOverlayDone(true);
    } catch {
      toast.error("Failed to save preferences. You can update them in Settings.");
      setOverlayDone(true);
    } finally {
      setSubmitting(false);
    }
  };

  if (verifying) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center">
        <div className="w-10 h-10 border-4 border-[#1E40AF] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center px-6 relative">

      {/* Background success card */}
      <div className="text-center max-w-md">
        <div className="w-20 h-20 bg-[#10B981]/10 rounded-full flex items-center justify-center mx-auto mb-6">
          <CheckCircle className="w-10 h-10 text-[#10B981]" />
        </div>
        <h1 className="text-2xl font-bold font-['Outfit'] text-[#0F172A] mb-2">
          {plan === "dfy" ? "Welcome to Done For You! 🎉" : "Welcome to Done With You! 🎉"}
        </h1>
        <p className="text-slate-600 mb-2">Your 7-day free trial has started. No charge until day 8.</p>
        <p className="text-sm text-slate-400">Your first AI scan is running in the background...</p>
      </div>

      {/* Personalization overlay — non-dismissible until complete */}
      {!overlayDone && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center px-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden">
            {/* Progress bar */}
            <div className="w-full bg-slate-100 h-1">
              <div className="bg-[#1E40AF] h-1 transition-all duration-500"
                style={{ width: `${(overlayStep / 4) * 100}%` }} />
            </div>

            <div className="p-8">
              {/* Step counter */}
              <div className="flex items-center justify-between mb-6">
                <span className="text-xs text-slate-400 font-medium">Question {overlayStep} of 4</span>
                <span className="text-xs text-[#1E40AF] font-semibold">
                  {overlayStep === 1 ? "Almost done!" : overlayStep === 4 ? "Last one!" : "Quick question"}
                </span>
              </div>

              {/* Step 1 — Competitors */}
              {overlayStep === 1 && (
                <div className="space-y-4">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-10 h-10 bg-[#1E40AF]/10 rounded-xl flex items-center justify-center">
                      <Users className="w-5 h-5 text-[#1E40AF]" />
                    </div>
                    <div>
                      <h3 className="font-bold text-[#0F172A] font-['Outfit']">Who are your competitors?</h3>
                      <p className="text-xs text-slate-500">We'll track their AI scores too (optional — add up to 5)</p>
                    </div>
                  </div>
                  <div className="space-y-2">
                    {personalization.competitors.map((c, i) => (
                      <input key={i} value={c} onChange={(e) => updateCompetitor(i, e.target.value)}
                        placeholder={`Competitor ${i + 1} website or name (optional)`}
                        className="w-full h-11 px-4 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:border-[#1E40AF] focus:outline-none"
                        style={{ color: "#000" }} />
                    ))}
                  </div>
                  <p className="text-xs text-slate-400">Skip this if you're not sure — you can add them later in Settings</p>
                </div>
              )}

              {/* Step 2 — Primary Goal */}
              {overlayStep === 2 && (
                <div className="space-y-4">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-10 h-10 bg-[#F97316]/10 rounded-xl flex items-center justify-center">
                      <Target className="w-5 h-5 text-[#F97316]" />
                    </div>
                    <div>
                      <h3 className="font-bold text-[#0F172A] font-['Outfit']">What's your #1 goal?</h3>
                      <p className="text-xs text-slate-500">We'll tailor your weekly tasks and action plan to this</p>
                    </div>
                  </div>
                  <div className="space-y-2">
                    {GOALS.map((goal) => (
                      <button key={goal.id}
                        onClick={() => setPersonalization((p) => ({ ...p, primary_goal: goal.id }))}
                        className={`w-full flex items-center gap-3 p-4 rounded-xl border-2 text-left transition-all ${
                          personalization.primary_goal === goal.id
                            ? "border-[#1E40AF] bg-[#1E40AF]/5"
                            : "border-slate-200 hover:border-slate-300"
                        }`}>
                        <span className="text-xl">{goal.icon}</span>
                        <span className={`text-sm font-medium ${personalization.primary_goal === goal.id ? "text-[#1E40AF]" : "text-[#0F172A]"}`}>
                          {goal.label}
                        </span>
                        {personalization.primary_goal === goal.id && (
                          <CheckCircle className="w-4 h-4 text-[#1E40AF] ml-auto flex-shrink-0" />
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Step 3 — How did you hear about us */}
              {overlayStep === 3 && (
                <div className="space-y-4">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-10 h-10 bg-[#10B981]/10 rounded-xl flex items-center justify-center">
                      <HelpCircle className="w-5 h-5 text-[#10B981]" />
                    </div>
                    <div>
                      <h3 className="font-bold text-[#0F172A] font-['Outfit']">How did you find ORDO?</h3>
                      <p className="text-xs text-slate-500">Helps us understand where to reach more businesses like yours</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    {SOURCES.map((s) => (
                      <button key={s.id}
                        onClick={() => setPersonalization((p) => ({ ...p, acquisition_source: s.id }))}
                        className={`p-3 rounded-xl border-2 text-left text-sm font-medium transition-all ${
                          personalization.acquisition_source === s.id
                            ? "border-[#1E40AF] bg-[#1E40AF]/5 text-[#1E40AF]"
                            : "border-slate-200 hover:border-slate-300 text-[#0F172A]"
                        }`}>
                        {s.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Step 4 — Additional context */}
              {overlayStep === 4 && (
                <div className="space-y-4">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-10 h-10 bg-[#7C3AED]/10 rounded-xl flex items-center justify-center">
                      <Zap className="w-5 h-5 text-[#7C3AED]" />
                    </div>
                    <div>
                      <h3 className="font-bold text-[#0F172A] font-['Outfit']">Anything else we should know?</h3>
                      <p className="text-xs text-slate-500">Special challenges, seasonal business, service areas — anything helps (optional)</p>
                    </div>
                  </div>
                  <textarea
                    value={personalization.additional_context}
                    onChange={(e) => setPersonalization((p) => ({ ...p, additional_context: e.target.value }))}
                    placeholder="E.g. We're busiest in summer, we serve a 30-mile radius, our biggest competitor just launched aggressive ads..."
                    rows={5} maxLength={1000}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:border-[#1E40AF] focus:outline-none resize-none"
                    style={{ color: "#000" }}
                  />
                  <p className="text-xs text-slate-400 text-right">{personalization.additional_context.length}/1000</p>
                </div>
              )}

              {/* Navigation */}
              <div className="flex gap-3 mt-6">
                {overlayStep > 1 && (
                  <button onClick={() => setOverlayStep(overlayStep - 1)}
                    className="px-5 py-3 border border-slate-200 rounded-xl text-sm text-slate-600 font-medium hover:bg-slate-50">
                    Back
                  </button>
                )}
                {overlayStep < 4 ? (
                  <button onClick={handleNext}
                    className="flex-1 bg-[#1E40AF] hover:bg-[#1E3A8A] text-white font-bold py-3 rounded-xl text-sm flex items-center justify-center gap-2">
                    Continue <ArrowRight className="w-4 h-4" />
                  </button>
                ) : (
                  <button onClick={handleFinish} disabled={submitting}
                    className="flex-1 bg-[#F97316] hover:bg-[#EA580C] text-white font-bold py-3 rounded-xl text-sm flex items-center justify-center gap-2 disabled:opacity-60">
                    {submitting
                      ? <div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                      : <><CheckCircle className="w-4 h-4" /> Go to My Dashboard</>
                    }
                  </button>
                )}
              </div>

              {/* Skip only on step 1 and 4 */}
              {(overlayStep === 1 || overlayStep === 4) && (
                <button onClick={overlayStep === 4 ? handleFinish : handleNext}
                  className="w-full text-xs text-slate-400 hover:text-slate-600 mt-3">
                  Skip this question
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* After overlay complete — go to dashboard */}
      {overlayDone && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center px-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-8 text-center">
            <CheckCircle className="w-12 h-12 text-[#10B981] mx-auto mb-4" />
            <h3 className="text-xl font-bold font-['Outfit'] text-[#0F172A] mb-2">All set!</h3>
            <p className="text-sm text-slate-500 mb-6">Your personalized dashboard is ready. Your first AI scan results will appear shortly.</p>
            <button onClick={() => navigate("/dashboard")}
              className="w-full bg-[#1E40AF] hover:bg-[#1E3A8A] text-white font-bold py-3 rounded-xl text-sm flex items-center justify-center gap-2">
              Go to My Dashboard <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaymentSuccess;
